// Accessible login overlay — shown before the first credit-costing AI op when
// the user is still anonymous (the login gate). Offers Apple / Google OAuth and
// an email magic link.
//
// IMPORTANT: OAuth and magic-link both complete asynchronously via a
// redirect / deep link, so this overlay cannot block-and-resume in one tap. It
// kicks off sign-in, tells the user to retry after returning, and resolves the
// gate as "not yet signed in" — the next generate() tap proceeds once the real
// session is present. Pure DOM, dependency-injected, jsdom-tested.

import { trapBackground } from './overlay-a11y';

export interface LoginDeps {
  /** Start OAuth sign-in (redirect/deep link). */
  onProvider: (provider: 'apple' | 'google') => unknown;
  /** Send a magic-link email. */
  onEmail: (email: string) => unknown;
  /** Called when the user dismisses without signing in. */
  onDismiss?: () => unknown;
  doc?: Document;
  prefersReducedMotion?: () => boolean;
}

export interface LoginHandle {
  show: () => void;
  close: () => void;
  readonly isOpen: boolean;
}

const STYLE_ID = 'jf-login-style';

const STYLES = `
.jf-login-overlay{position:fixed;inset:0;z-index:2200;display:flex;align-items:center;
  justify-content:center;padding:16px;background:rgba(15,23,42,.78);backdrop-filter:blur(4px);}
.jf-login-dialog{background:#1e293b;color:#f1f5f9;width:min(420px,100%);border-radius:16px;
  padding:24px;box-shadow:0 20px 60px rgba(0,0,0,.5);font-family:system-ui,-apple-system,sans-serif;}
.jf-login-anim{animation:jf-login-in .18s ease-out;}
@keyframes jf-login-in{from{opacity:0;transform:translateY(8px);}to{opacity:1;transform:none;}}
.jf-login-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;}
.jf-login-title{font-size:1.25rem;font-weight:700;margin:0;}
.jf-login-close{background:transparent;border:0;color:#cbd5e1;font-size:1.5rem;line-height:1;
  cursor:pointer;min-width:44px;min-height:44px;border-radius:8px;}
.jf-login-close:hover,.jf-login-close:focus-visible{background:rgba(255,255,255,.1);color:#fff;}
.jf-login-sub{margin:4px 0 16px;color:#cbd5e1;font-size:.92rem;}
.jf-login-provider{display:block;width:100%;min-height:48px;margin-bottom:10px;border-radius:10px;
  border:1px solid #334155;background:#0f172a;color:#f1f5f9;font-size:.95rem;font-weight:600;cursor:pointer;}
.jf-login-provider:hover,.jf-login-provider:focus-visible{background:#27364b;}
.jf-login-sep{display:flex;align-items:center;gap:8px;color:#64748b;font-size:.8rem;margin:12px 0;}
.jf-login-sep::before,.jf-login-sep::after{content:"";flex:1;height:1px;background:#334155;}
.jf-login-email{display:flex;gap:8px;}
.jf-login-email input{flex:1;min-height:44px;border-radius:10px;border:1px solid #334155;
  background:#0f172a;color:#f1f5f9;padding:0 12px;font-size:.95rem;}
.jf-login-email button{min-height:44px;padding:0 16px;border:0;border-radius:10px;background:#e67e22;
  color:#fff;font-weight:700;cursor:pointer;}
.jf-login-email button:hover,.jf-login-email button:focus-visible{background:#d35400;}
.jf-login-hint{margin-top:14px;color:#94a3b8;font-size:.78rem;line-height:1.5;}
@media(prefers-reduced-motion:reduce){.jf-login-anim{animation:none;}}
`;

const FOCUSABLE =
  'a[href],button:not([disabled]),input,select,textarea,[tabindex]:not([tabindex="-1"])';

function defaultReducedMotion(): boolean {
  try {
    return (
      typeof globalThis.matchMedia === 'function' &&
      globalThis.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  } catch {
    return false;
  }
}

export function mountLogin(deps: LoginDeps): LoginHandle {
  const doc = deps.doc ?? document;
  const reducedMotion = deps.prefersReducedMotion ?? defaultReducedMotion;

  let overlay: HTMLElement | null = null;
  let lastFocused: Element | null = null;
  let open = false;
  let releaseBackground: (() => void) | null = null;

  function ensureStyle(): void {
    if (doc.getElementById(STYLE_ID)) return;
    const style = doc.createElement('style');
    style.id = STYLE_ID;
    style.textContent = STYLES;
    (doc.head ?? doc.body ?? doc.documentElement).appendChild(style);
  }

  function render(): HTMLElement {
    const titleId = 'jf-login-title';
    const descId = 'jf-login-desc';

    const ov = doc.createElement('div');
    ov.className = 'jf-login-overlay';

    const dialog = doc.createElement('div');
    dialog.className = 'jf-login-dialog' + (reducedMotion() ? '' : ' jf-login-anim');
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', titleId);
    dialog.setAttribute('aria-describedby', descId);

    dialog.innerHTML = `
      <div class="jf-login-head">
        <h2 class="jf-login-title" id="${titleId}">Anmelden für KI</h2>
        <button type="button" class="jf-login-close" aria-label="Schließen">&times;</button>
      </div>
      <p class="jf-login-sub" id="${descId}">Melde dich an, um die KI-Bildfunktionen zu nutzen. Zeichnen bleibt kostenlos und ohne Anmeldung.</p>
      <button type="button" class="jf-login-provider" data-provider="apple">Mit Apple anmelden</button>
      <button type="button" class="jf-login-provider" data-provider="google">Mit Google anmelden</button>
      <div class="jf-login-sep">oder</div>
      <form class="jf-login-email" data-act="email">
        <input type="email" name="email" placeholder="deine@email.at" aria-label="E-Mail-Adresse" required />
        <button type="submit">Link senden</button>
      </form>
      <p class="jf-login-hint">Nach der Anmeldung tippe erneut auf Generieren. Die ersten KI-Zauber gehen aufs Haus.</p>`;

    ov.appendChild(dialog);
    return ov;
  }

  function onClick(e: Event): void {
    const t = e.target as HTMLElement;
    if (t.classList.contains('jf-login-overlay')) return dismiss();
    if (t.classList.contains('jf-login-close')) return dismiss();
    const provider = t.closest<HTMLElement>('.jf-login-provider')?.dataset.provider;
    if (provider === 'apple' || provider === 'google') {
      deps.onProvider(provider);
      close();
    }
  }

  function onSubmit(e: Event): void {
    e.preventDefault();
    const form = (e.target as HTMLElement)?.closest<HTMLFormElement>('.jf-login-email');
    if (!form) return;
    const input = form.querySelector<HTMLInputElement>('input[name="email"]');
    const email = input?.value.trim();
    if (email) {
      deps.onEmail(email);
      close();
    }
  }

  function onKeydown(e: KeyboardEvent): void {
    if (!overlay) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      dismiss();
      return;
    }
    if (e.key !== 'Tab') return;
    const focusables = Array.from(overlay.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (focusables.length === 0) return;
    const first = focusables[0]!;
    const last = focusables[focusables.length - 1]!;
    if (e.shiftKey && doc.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && doc.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function dismiss(): void {
    close();
    deps.onDismiss?.();
  }

  function show(): void {
    if (overlay) return;
    ensureStyle();
    lastFocused = doc.activeElement;
    overlay = render();
    (doc.body ?? doc.documentElement).appendChild(overlay);
    releaseBackground = trapBackground(overlay);
    overlay.addEventListener('click', onClick);
    overlay.addEventListener('submit', onSubmit);
    doc.addEventListener('keydown', onKeydown, true);
    open = true;
    overlay.querySelector<HTMLElement>('.jf-login-provider')?.focus();
  }

  function close(): void {
    if (!overlay) return;
    overlay.removeEventListener('click', onClick);
    overlay.removeEventListener('submit', onSubmit);
    overlay.remove();
    overlay = null;
    open = false;
    releaseBackground?.();
    releaseBackground = null;
    doc.removeEventListener('keydown', onKeydown, true);
    if (lastFocused instanceof HTMLElement) lastFocused.focus();
  }

  return {
    show,
    close,
    get isOpen() {
      return open;
    },
  };
}
